import {
	NotificationType,
	SmsCredentialMode,
	type SmsSettingsDTO,
	type SmsSettingsInput,
	type SmsTwilioCredentialsInput,
	TRANSACTIONAL_ACCESS_TYPES,
} from '@today/types'

// The SMS settings form's editable state and its mapping to the PUT body
// (TO-383 REQ-014): text inputs are kept as typed and normalized on submit.

/** API field path (`details.field`) → message to show next to that field. */
export type SmsSettingsFieldErrors = Readonly<Record<string, string>>

/**
 * The notification types an admin may allow by SMS: the test SMS and the
 * transactional landing access codes (TO-478) are never subject to the allowlist.
 */
export const SMS_SELECTABLE_NOTIFICATION_TYPES: readonly NotificationType[] = Object.values(NotificationType).filter(
	(type) => type !== NotificationType.SMS_TEST && !TRANSACTIONAL_ACCESS_TYPES.has(type),
)

/** `content_published` → `Content published`. */
export const notificationTypeLabel = (type: NotificationType): string => {
	const words = type.split('_').join(' ')
	return words.charAt(0).toUpperCase() + words.slice(1)
}

export interface SmsSettingsDraft {
	mode: SmsCredentialMode
	fromIdentity: string
	credentials: SmsTwilioCredentialsInput
	countries: string
	cap: string
	types: NotificationType[] | null
}

export const EMPTY_CREDENTIALS: SmsTwilioCredentialsInput = { accountSid: '', apiKeySid: '', apiKeySecret: '' }

/** The stored settings as form state; credentials are write-only, so never prefilled. */
export const draftOf = (settings: SmsSettingsDTO): SmsSettingsDraft => ({
	mode: settings.mode,
	fromIdentity: settings.fromIdentity ?? '',
	credentials: EMPTY_CREDENTIALS,
	countries: settings.allowedCountries?.join(', ') ?? '',
	cap: settings.monthlySpendCapEur === null ? '' : String(settings.monthlySpendCapEur),
	types: settings.smsNotificationTypes ? [...settings.smsNotificationTypes] : null,
})

/** `" es, pt ,"` → `["ES","PT"]`; nothing → `null` (any country). */
const countriesOf = (text: string): string[] | null => {
	const codes = text
		.split(',')
		.map((code) => code.trim().toUpperCase())
		.filter((code) => code !== '')
	return codes.length === 0 ? null : codes
}

/** Credentials are sent only under BYO and only when the whole trio is filled (empty keeps the stored ones). */
const credentialsOf = (draft: SmsSettingsDraft): SmsTwilioCredentialsInput | undefined => {
	if (draft.mode !== SmsCredentialMode.BYO) return undefined
	const { accountSid, apiKeySid, apiKeySecret } = draft.credentials
	return accountSid !== '' && apiKeySid !== '' && apiKeySecret !== '' ? draft.credentials : undefined
}

/** The PUT body: empty sender, countries and cap mean `null` ("platform sender", "any country", "no cap"). */
export const submissionOf = (draft: SmsSettingsDraft): SmsSettingsInput => {
	const credentials = credentialsOf(draft)
	const fromIdentity = draft.fromIdentity.trim()
	const cap = draft.cap.trim()
	return {
		mode: draft.mode,
		fromIdentity: fromIdentity === '' ? null : fromIdentity,
		...(credentials ? { credentials } : {}),
		allowedCountries: countriesOf(draft.countries),
		monthlySpendCapEur: cap === '' ? null : Number(cap),
		smsNotificationTypes: draft.types,
	}
}
